import type { Contact } from '@ksyos/http';
import { Link, List, Text, Timeline } from '@ksyos/design-system';
import { orderBy } from 'lodash';
import { useEffect, useState } from 'react';
import { mijnKsyosClient } from '../../../../services/api/mijnKsyosClient';
import { CollapseCard } from './CollapseCard';

type OptometryStepsCardProps = Readonly<{
    isInitiallyOpen?: boolean;
}>;

const itemHeadingSize = 'md';
const listStyleType = 'bullet';

export function OptometryStepsCard({ isInitiallyOpen }: OptometryStepsCardProps) {
    const [latestContact, setLatestContact] = useState<Contact>();
    const organisatieNaam = latestContact?.locatie.organisatieNaam;
    const telefoonnummer = latestContact?.locatie.contactgegevens.telefoonnummers.at(0)?.telefoonnummer;

    useEffect(() => {
        getLatestContact().then(setLatestContact);
    }, []);

    return (
        <CollapseCard
            isInitiallyOpen={isInitiallyOpen}
            title="Vervolgstappen"
        >
            <Timeline>
                <Timeline.Item>
                    <Timeline.ItemHeading size={itemHeadingSize}>Maak een afspraak</Timeline.ItemHeading>
                    <Text>
                        Bel om een afspraak te maken
                        {organisatieNaam && ` bij ${organisatieNaam}`}
                        {telefoonnummer && `. Deze optiekzaak is bereikbaar op het telefoonnummer `}
                        {telefoonnummer && (
                            <Link
                                to={`tel:${telefoonnummer}`}
                                variant="link"
                            >
                                {telefoonnummer}
                            </Link>
                        )}
                    </Text>
                </Timeline.Item>
                <Timeline.Item>
                    <Timeline.ItemHeading size={itemHeadingSize}>Wat neem je mee?</Timeline.ItemHeading>
                    <List styleType={listStyleType}>
                        <List.Item>Je bril of contactlenzen</List.Item>
                        <List.Item>Een lijst van de medicijnen die je gebruikt</List.Item>
                        <List.Item>Een verwijzing van de huisarts (als je die hebt)</List.Item>
                        <List.Item>Je legitimatiebewijs</List.Item>
                        <List.Item>Je verzekeringspas</List.Item>
                        <List.Item>Een zonnebril</List.Item>
                        <List.Item>Soms krijg je druppels in je ogen. Kom daarom niet met de auto</List.Item>
                    </List>
                </Timeline.Item>
                <Timeline.Item>
                    <Timeline.ItemHeading size={itemHeadingSize}>Het onderzoek</Timeline.ItemHeading>
                    <List styleType={listStyleType}>
                        <List.Item>We kijken hoe het met je ogen gaat</List.Item>
                        <List.Item>Het onderzoek duurt tussen de 30 minuten en 1 uur</List.Item>
                        <List.Item>Je krijgt altijd een oogmeting</List.Item>
                        <List.Item>We maken een foto van je ogen</List.Item>
                        <List.Item>We meten de druk in je ogen</List.Item>
                        <List.Item>Soms doen we nog extra onderzoeken</List.Item>
                    </List>
                </Timeline.Item>
                <Timeline.Item>
                    <Timeline.ItemHeading size={itemHeadingSize}>Beoordeling door de oogarts</Timeline.ItemHeading>
                    <Text>
                        Als het nodig is, worden de resultaten van het onderzoek naar een oogarts gestuurd. Binnen twee
                        werkdagen krijgt de optometrist de beoordeling van de oogarts van Ksyos
                    </Text>
                </Timeline.Item>
                <Timeline.Item>
                    <Timeline.ItemHeading size={itemHeadingSize}>Vervolg en advies</Timeline.ItemHeading>
                    <Text>Als het nodig is, wordt een vervolgafspraak gemaakt of een behandelplan voorgesteld</Text>
                </Timeline.Item>
            </Timeline>
        </CollapseCard>
    );
}

async function getLatestContact(): Promise<Contact | undefined> {
    const response = await mijnKsyosClient.contacten.getContacten();
    if (response.status !== 200) {
        return;
    }

    const optometryContacten = response.body.contacten.filter(
        (contact) => contact.type === 'optometryProcess' || contact.type === 'optometryRequest'
    );

    return orderBy(optometryContacten, 'datumTijd', 'desc').at(0);
}
